// frontend/src/utils/salePdfGenerator.ts
import { jsPDF } from 'jspdf';

interface SaleItem {
  quantity: number;
  productName: string;
  unitPrice: number;
  total: number;
}

interface Sale {
  id: number;
  customerName?: string;
  customerPhone?: string;
  total: number;
  shippingCost?: number;
  alterationCost?: number;
  discount?: number;
  paymentMethod: string;
  saleType: string;
  createdAt: string;
  eventDate?: string;
  items?: (SaleItem & { product?: any; isPreOrder?: boolean })[];
  seller?: { username?: string; email?: string };
  folio?: string;
  notes?: string;
  status?: string;
  amountPaid?: number;
  payments?: any[];
}

// ─── Cache del logo renderizado via canvas ────────────────────────────────────
// jsPDF no puede parsear PNGs con canal alpha complejo directamente.
// Solución: dibujar la imagen en un canvas y exportar como PNG limpio.
let LOGO_B64: string | null = null;
let LOGO_W = 0;
let LOGO_H = 0;

async function getLogoB64(): Promise<{ b64: string; w: number; h: number } | null> {
  if (LOGO_B64) return { b64: LOGO_B64, w: LOGO_W, h: LOGO_H };
  try {
    const mod = await import('../assets/Maria_Regna_Logotipo.png');
    const url: string = mod.default;

    return new Promise((resolve) => {
      const img = new Image();
      img.crossOrigin = 'anonymous';

      img.onload = () => {
        // Escalar proporcionalmente dentro del máximo permitido (60mm x 30mm)
        const maxW = 60, maxH = 30;
        const ratio = Math.min(maxW / img.naturalWidth, maxH / img.naturalHeight);
        LOGO_W = img.naturalWidth * ratio;
        LOGO_H = img.naturalHeight * ratio;

        // Renderizar en canvas para generar un PNG limpio compatible con jsPDF
        const canvas = document.createElement('canvas');
        // Escala 3x para mayor calidad en el PDF
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        const ctx = canvas.getContext('2d')!;
        // Fondo blanco para evitar transparencia problemática
        ctx.fillStyle = '#ffffff';
        ctx.fillRect(0, 0, canvas.width, canvas.height);
        ctx.drawImage(img, 0, 0);

        LOGO_B64 = canvas.toDataURL('image/jpeg', 0.95);
        resolve({ b64: LOGO_B64, w: LOGO_W, h: LOGO_H });
      };

      img.onerror = () => resolve(null);
      img.src = url;
    });
  } catch {
    return null;
  }
}

// ─── Generador principal ───────────────────────────────────────────────────────
export const generateSalePDF = async (sale: Sale): Promise<void> => {
  if (!sale || !sale.id) {
    console.error('Datos de venta invalidos:', sale);
    throw new Error('Datos de venta invalidos');
  }

  const total = Number(sale.total || 0);
  const amountPaid = Number(sale.amountPaid || 0);
  const totalPaidCalc = sale.payments?.reduce((s: number, p: any) => s + Number(p.amount), 0) || amountPaid;
  const remaining = Math.max(0, total - totalPaidCalc);

  const parentFolio = (sale as any).parentSale?.folio || ((sale as any).parentSaleId ? `#${(sale as any).parentSaleId}` : null);
  const ctx = (sale as any).saleContext;
  const globalTotal = ctx ? Number(ctx.totalGlobal) : null;
  const globalPaid = ctx ? Number(ctx.totalPaidGlobal) : null;
  const globalBalance = ctx ? (ctx.balance !== undefined ? Number(ctx.balance) : null) : null;

  const isAbono = (sale as any).saleType === 'ABONO' || !!(sale as any).parentSaleId;
  
  // Para un abono, es liquidación SOLO si el saldo GLOBAL es cero
  const isLiquidacion = (sale as any).saleType === 'LIQUIDACION' || (isAbono && globalBalance !== null && globalBalance < 0.01);
  
  const isLayaway = !isAbono && (
    sale.status === 'APARTADO' || sale.status === 'PENDIENTE' || sale.status === 'PRE-ORDER' ||
    (totalPaidCalc < total && totalPaidCalc > 0)
  );
  const isLiquidation = !isAbono && sale.status === 'COMPLETADA' && totalPaidCalc >= total - 0.01 && (sale.payments?.length || 0) > 1;
  const showPaymentHistory = isAbono || isLayaway || isLiquidation || isLiquidacion;
  const hasPreOrderItems = sale.items?.some((i: any) => i.isPreOrder) || sale.status === 'PRE-ORDER';
  const shippingCost = Number(sale.shippingCost || 0);
  const alterationCost = Number(sale.alterationCost || 0);
  const discount = Number(sale.discount || 0);

  const receiptType = isLiquidacion
    ? 'COMPROBANTE DE LIQUIDACION'
    : isAbono
      ? 'COMPROBANTE DE ABONO'
      : isLiquidation
        ? 'COMPROBANTE DE LIQUIDACION'
        : (isLayaway ? 'COMPROBANTE DE ABONO' : 'COMPROBANTE DE VENTA');


  // ── Intenta cargar el logo ──────────────────────────────────────────────────
  const logo = await getLogoB64();

  // ── Estimación de altura dinámica ──────────────────────────────────────────
  let estimatedHeight = 10; // padding inicial
  estimatedHeight += logo ? logo.h + 8 : 14; // logo o texto
  estimatedHeight += 5 + 5;  // tagline + divider
  if (hasPreOrderItems || (sale.notes?.includes('MAYOREO'))) estimatedHeight += 8;
  estimatedHeight += 20;     // folio + cliente
  if (sale.eventDate) estimatedHeight += 5;
  if (isAbono && parentFolio) {
    estimatedHeight += (globalTotal !== null ? 35 : 15); // Referencia de venta original
  }
  estimatedHeight += 3;      // separador
  estimatedHeight += (sale.items?.length || 0) * 14;   // items
  estimatedHeight += 5;      // separador
  estimatedHeight += 12;     // total
  if (discount > 0 || shippingCost > 0 || alterationCost > 0) estimatedHeight += 15;
  if (showPaymentHistory) {
    estimatedHeight += 8;    // abonado + restante
    estimatedHeight += (sale.payments?.length || 0) * 4;
  } else {
    estimatedHeight += 5;
  }
  estimatedHeight += 65; // footer (gracias + 2 cajas + whatsapp + ornamento + MARGEN)
  if (hasPreOrderItems) estimatedHeight += 10;

  // ── Crear PDF ──────────────────────────────────────────────────────────────
  const PAGE_W = 80;
  const doc = new jsPDF({
    orientation: 'portrait',
    unit: 'mm',
    format: [PAGE_W, estimatedHeight],
  });

  const x = (w: number) => (PAGE_W - w) / 2; // centro horizontal
  let y = 8;

  // Helpers
  const txt = (text: string, xPos: number, yPos: number, opts?: any) => doc.text(text, xPos, yPos, opts);
  const line = (y1: number, dashed = false) => {
    doc.setLineWidth(dashed ? 0.2 : 0.4);
    if (dashed) {
      // Línea punteada manual
      doc.setLineDashPattern([1, 1], 0);
    } else {
      doc.setLineDashPattern([], 0);
    }
    doc.line(5, y1, 75, y1);
    doc.setLineDashPattern([], 0);
  };
  const setTxt = (size: number, style: 'normal' | 'bold' | 'italic' = 'normal') => {
    doc.setFontSize(size);
    doc.setFont('helvetica', style);
    doc.setTextColor(0, 0, 0);
  };

  // ── HEADER ─────────────────────────────────────────────────────────────────
  if (logo) {
    const logoX = x(logo.w);
    doc.addImage(logo.b64, 'JPEG', logoX, y, logo.w, logo.h);
    y += logo.h + 3;
  } else {
    setTxt(14, 'bold');
    txt('BOUTIQUE MARIA REGNA', 40, y, { align: 'center' });
    y += 6;
  }

  setTxt(7.5, 'normal');
  txt('M O D A   &   A L T A   C O S T U R A', 40, y, { align: 'center' });
  y += 4;

  // Badge de tipo de comprobante
  setTxt(8, 'bold');
  doc.setFillColor(0, 0, 0);
  const badgeW = receiptType === 'COMPROBANTE DE LIQUIDACION' ? 68 : receiptType === 'COMPROBANTE DE ABONO' ? 56 : 56;
  doc.rect(x(badgeW), y, badgeW, 5, 'F');
  doc.setTextColor(255, 255, 255);
  txt(receiptType, 40, y + 3.5, { align: 'center' });
  doc.setTextColor(0, 0, 0);
  y += 8;

  if (hasPreOrderItems) {
    doc.setFillColor(0, 0, 0);
    doc.rect(x(50), y, 50, 5, 'F');
    doc.setTextColor(255, 255, 255);
    txt('PEDIDO ESPECIAL', 40, y + 3.5, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    y += 8;
  }

  if (sale.notes?.includes('MAYOREO')) {
    doc.setFillColor(0, 0, 0);
    doc.rect(x(60), y, 60, 5, 'F');
    doc.setTextColor(255, 255, 255);
    txt('COMPRA POR MAYOREO', 40, y + 3.5, { align: 'center' });
    doc.setTextColor(0, 0, 0);
    y += 8;
  }

  y += 1;
  doc.setLineWidth(0.6);
  doc.setLineDashPattern([], 0);
  doc.line(5, y, 75, y);
  y += 5;

  // ── FOLIO + FECHA ──────────────────────────────────────────────────────────
  setTxt(7, 'normal');
  txt('FOLIO', 5, y);
  const saleDate = new Date(sale.createdAt);
  const dateStr = saleDate.toLocaleDateString('es-MX', { day: '2-digit', month: 'short', year: 'numeric' });
  const timeStr = saleDate.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  txt(`${dateStr} ${timeStr}`, 75, y, { align: 'right' });
  y += 3.5;

  setTxt(11, 'bold');
  txt(`#${sale.folio || sale.id}`, 5, y);
  y += 5;

  line(y, true);
  y += 4;

  // ── CLIENTE ────────────────────────────────────────────────────────────────
  setTxt(7, 'bold');
  txt('CLIENTE', 5, y);
  y += 3.5;

  setTxt(10, 'bold');
  txt(sale.customerName || 'Publico General', 5, y);
  y += 3.5;

  setTxt(8, 'normal');
  txt(`Tel: ${sale.customerPhone || 'N/A'}`, 5, y);
  y += 3.5;

  if (sale.eventDate) {
    setTxt(9, 'bold');
    const evDate = new Date(sale.eventDate).toLocaleDateString('es-MX', { timeZone: 'UTC', day: '2-digit', month: 'long', year: 'numeric' });
    txt(`Fecha Evento: ${evDate}`, 5, y);
    y += 4;
  }

  line(y, true);
  y += 4;

  // ── REFERENCIA DE VENTA ORIGINAL (solo abonos/liquidaciones) ───────────────
  if (isAbono && parentFolio) {
    setTxt(7, 'bold');
    txt('REFERENCIA DE VENTA', 5, y);
    y += 3.5;

    // Caja con borde
    doc.setLineWidth(0.5);
    doc.setDrawColor(0, 0, 0);
    const refBoxH = globalTotal !== null ? 22 : 10;
    doc.rect(5, y - 1, 70, refBoxH);

    setTxt(10, 'bold');
    txt(`Folio original: ${parentFolio}`, 8, y + 3.5);
    y += 7;

    if (globalTotal !== null) {
      setTxt(8, 'normal');
      txt('Total de la venta:', 8, y);
      txt(`$${globalTotal.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 72, y, { align: 'right' });
      y += 3.5;
      txt('Total abonado:', 8, y);
      txt(`$${(globalPaid || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 72, y, { align: 'right' });
      y += 3.5;
      setTxt(9, 'bold');
      const balLabel = (globalBalance || 0) < 0.01 ? 'SALDO: LIQUIDADO' : 'Saldo pendiente:';
      const balValue = (globalBalance || 0) < 0.01 ? 'PAGADO' : `$${(globalBalance || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
      txt(balLabel, 8, y);
      txt(balValue, 72, y, { align: 'right' });
      y += 5;
    }
    y += 3;
  }

  // ── PRENDAS (solo si hay items) ────────────────────────────────────────────
  if (sale.items && Array.isArray(sale.items) && sale.items.length > 0) {
    setTxt(7, 'bold');
    txt('PRENDAS', 5, y);
    y += 4;
  }

  if (sale.items && Array.isArray(sale.items)) {
    sale.items.forEach((item) => {
      const p = item.product || {};
      const codigoProveedor = p.codigo_proveedor || p.marca || 'GENERICO';
      const codigo = p.cod_producto || '';
      const talla = p.talla || '-';
      const color = p.color || '-';
      const itemTotal = Number(item.total || 0);

      setTxt(9, 'bold');
      txt(`${item.quantity} x ${codigoProveedor}`, 5, y);
      txt(`$${itemTotal.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 75, y, { align: 'right' });
      y += 3.5;

      setTxt(8, 'normal');
      txt(`Mod: ${codigo}  |  Talla: ${talla}  |  Color: ${color}`, 5, y);
      y += 3.5;

      if (item.isPreOrder) {
        setTxt(7, 'italic');
        txt('* Pedido Especial', 5, y);
        y += 3;
      }

      // Separador punteado entre items
      doc.setLineWidth(0.1);
      doc.setLineDashPattern([0.5, 0.5], 0);
      doc.line(5, y, 75, y);
      doc.setLineDashPattern([], 0);
      y += 2;
    });
  }

  y += 2;
  doc.setLineWidth(0.5);
  doc.setLineDashPattern([], 0);
  doc.line(5, y, 75, y);
  y += 5;

  // ── TOTALES ────────────────────────────────────────────────────────────────
  if (discount > 0) {
    setTxt(8, 'italic');
    txt('Descuento:', 55, y, { align: 'right' });
    txt(`-$${discount.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 75, y, { align: 'right' });
    y += 3.5;
  }
  if (shippingCost > 0) {
    setTxt(8, 'italic');
    txt('Envio:', 55, y, { align: 'right' });
    txt(`+$${shippingCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 75, y, { align: 'right' });
    y += 3.5;
  }
  if (alterationCost > 0) {
    setTxt(8, 'italic');
    txt('Arreglos:', 55, y, { align: 'right' });
    txt(`+$${alterationCost.toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 75, y, { align: 'right' });
    y += 3.5;
  }

  setTxt(12, 'bold');
  txt('TOTAL:', 52, y, { align: 'right' });
  txt(`$${total.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 75, y, { align: 'right' });
  y += 5;

  if (showPaymentHistory) {
    setTxt(9, 'normal');
    txt('Total Abonado:', 52, y, { align: 'right' });
    txt(`$${totalPaidCalc.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 75, y, { align: 'right' });
    y += 4;

    if (remaining > 0.01) {
      setTxt(11, 'bold');
      txt('SALDO RESTANTE:', 52, y, { align: 'right' });
      txt(`$${remaining.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, 75, y, { align: 'right' });
      y += 5;
    } else {
      setTxt(10, 'bold');
      txt('LIQUIDADO - PAGADO', 40, y, { align: 'center' });
      y += 5;
    }

    // Historial de abonos
    const allPayments = sale.payments && sale.payments.length > 0
      ? sale.payments
      : amountPaid > 0 ? [{ createdAt: sale.createdAt, method: sale.paymentMethod || 'Pago', amount: amountPaid }] : [];

    if (allPayments.length > 0) {
      setTxt(7, 'bold');
      txt('HISTORIAL DE ABONOS', 5, y);
      y += 3.5;
      setTxt(8, 'normal');
      allPayments.forEach((p: any) => {
        const d = new Date(p.createdAt).toLocaleDateString('es-MX');
        txt(`${d} - ${p.method}`, 5, y);
        txt(`$${Number(p.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}`, 75, y, { align: 'right' });
        y += 3.5;
      });
      y += 2;
    }
  } else {
    setTxt(8, 'normal');
    txt(`Metodo de pago: ${sale.paymentMethod}`, 5, y);
    y += 5;
  }

  // ── FOOTER ─────────────────────────────────────────────────────────────────
  y += 2;
  doc.setLineWidth(0.5);
  doc.setLineDashPattern([], 0);
  doc.line(5, y, 75, y);
  y += 5;

  setTxt(8, 'italic');
  txt('¡Gracias por su preferencia!', 40, y, { align: 'center' });
  y += 6;

  // Caja: NO CAMBIOS NI DEVOLUCIONES
  doc.setLineWidth(0.5);
  doc.setDrawColor(0, 0, 0);
  doc.rect(5, y, 70, 10);
  setTxt(8.5, 'bold');
  txt('NO CAMBIOS NI DEVOLUCIONES', 40, y + 4.5, { align: 'center' });
  setTxt(7, 'normal');
  txt('Politica oficial de la boutique', 40, y + 8, { align: 'center' });
  y += 14;

  // Caja: Apartado 30 dias
  doc.setLineWidth(0.5);
  doc.rect(5, y, 70, 10);
  setTxt(8.5, 'bold');
  txt('Apartado valido por 30 dias', 40, y + 4.5, { align: 'center' });
  setTxt(7, 'normal');
  txt('Pasado este plazo se pierde el anticipo', 40, y + 8, { align: 'center' });
  y += 14;

  // Aviso pre-order
  if (hasPreOrderItems) {
    setTxt(8, 'bold');
    txt('Contacto Modistas:', 40, y, { align: 'center' });
    y += 3.5;
    txt('CARMEN +52 449 306 0184', 40, y, { align: 'center' });
    y += 3.5;
    txt('PILAR +52 449 358 9479', 40, y, { align: 'center' });
    y += 5;
  }

  setTxt(9, 'bold');
  txt('WhatsApp de atencion: 449 436 2610', 40, y, { align: 'center' });
  y += 4;

  setTxt(7, 'normal');
  txt('- - - - - - -', 40, y, { align: 'center' });

  // ── Guardar ────────────────────────────────────────────────────────────────
  doc.save(`recibo-${sale.folio || sale.id}.pdf`);
};
